import type { JSX, PropsWithChildren } from 'react';
import { styled } from 'styled-components';
import { useColorPickerState } from './ColorPickerContext';
import { DEFAULT_SWATCH_DIAMETER_PX } from './consts';

const ColorSwatchDiv = styled.div<{
  $selected?: boolean;
  $diameterPx: number;
}>`
  aspect-ratio: 1;
  width: ${({ $diameterPx }) => `${$diameterPx}px`};
  border-radius: var(--wui-border-radius-rounded);
  box-shadow: inset 0 0 1px 0 var(--wui-color-border-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  outline: ${({ $selected }) => {
    if ($selected) {
      return '2px solid var(--wui-color-focus-ring-info)';
    }
    return 'none';
  }};
  outline-offset: 2px;
  transform: ${({ $selected }) => ($selected ? 'scale(0.9)' : 'scale(1)')};
`;

const DerivativeHalfCircle = styled.div<{
  $color: string;
}>`
  position: absolute;
  right: 0;
  top: 0;
  width: 50%;
  height: 100%;
  background-color: ${({ $color }) => $color};
  border-radius: 0 var(--wui-border-radius-rounded) var(--wui-border-radius-rounded) 0;
`;

export type ColorSwatchProps = PropsWithChildren<{
  derivativeIsSelected?: boolean;
  diameterPx?: number;
  isSelected?: boolean;
  value: string;
}>;

export const ColorSwatch = ({
  children,
  diameterPx = DEFAULT_SWATCH_DIAMETER_PX,
  isSelected = false,
  derivativeIsSelected = false,
  value,
}: ColorSwatchProps): JSX.Element => {
  const { valueAsHex: colorPickerValueAsHex } = useColorPickerState();
  return (
    <ColorSwatchDiv
      $diameterPx={diameterPx}
      $selected={isSelected || derivativeIsSelected}
      aria-label={`Color swatch: ${value}`}
      role="img"
      style={{
        backgroundColor: value,
      }}
    >
      {derivativeIsSelected ? <DerivativeHalfCircle $color={colorPickerValueAsHex} /> : null}
      {children}
    </ColorSwatchDiv>
  );
};
